import { NullableResultPromise } from "../base-types";
import { IExtendedContentHubClient } from "../clients/extended-client";
import { IRule, Rule } from "../contracts/policies/rule";
import { RuleType } from "../contracts/policies/rule-type";
import Link from "../link";
import { RuleResource } from "../models/policies/rule-resource";
import { ConditionMapper } from "./condition-mapper";
import { RuleTypeMapper } from "./rule-type-mapper";

export class RulesMapper {
    /**
     * Map rule resources to rules.
     * @param client - An instance of an extended Content Hub client
     * @param ruleResources - A collection of resources to map
     * @returns An array of rules.
     */
    static async mapToRulesAsync(
        client: IExtendedContentHubClient,
        ruleResources: Array<RuleResource>
    ): Promise<Array<IRule>> {
        if (ruleResources == null || ruleResources.length === 0) {
            return [];
        }

        const list: Array<IRule> = [];

        for (const ruleResource of ruleResources) {
            const rule = await this.mapToRuleAsync(client, ruleResource);
            if (rule != null) {
                list.push(rule);
            }
        }

        return list;
    }

    /**
     * Map a rule resource to a rule instance.
     * @param client - An instance of an extended Content Hub client
     * @param ruleResource - A rule resource
     *
     * @returns A rule or null.
     */
    static async mapToRuleAsync(
        client: IExtendedContentHubClient,
        ruleResource: RuleResource
    ): NullableResultPromise<IRule> {
        if (ruleResource == null) {
            return null;
        }

        const conditions = await ConditionMapper.mapToConditionsAsync(client, ruleResource.conditions);
        //TODO Decide if we need a Guid type
        const guid = ruleResource.identifier;
        const type: RuleType = RuleTypeMapper.mapToRuleType(ruleResource.type);
        const definitionNames = await client.linkHelper.namesFromDefinitionsAsync(ruleResource.definitions);

        const sdkRule: Rule = new Rule({
            conditions: conditions,
            createdByLoggedOnUser: ruleResource.createdByLoggedOnUser,
            definitionNames: definitionNames.filter(name => name != null),
            isSystemOwned: ruleResource.isSystemOwned,
            permissions: ruleResource.permissions,
            type: type,
        });
        sdkRule.setIdentifier(guid);

        return sdkRule;
    }

    /**
     * Map rule instances to rule resources.
     * @param client - An instance of an extended Content Hub client
     * @param rules - A collection of rules
     * @returns An array of rule resources.
     */
    static async mapToRuleResourcesAsync(
        client: IExtendedContentHubClient,
        rules: Array<IRule>
    ): Promise<Array<RuleResource>> {
        if (rules == null || rules.length === 0) {
            return [];
        }

        const list: Array<RuleResource> = [];

        for (const rule of rules) {
            const ruleResource = await this.mapToRuleResourceAsync(client, rule);
            if (ruleResource != null) {
                list.push(ruleResource);
            }
        }

        return list;
    }

    /**
     * Map a rule to a rule resource.
     * @param client - An instance of an extended Content Hub client
     * @param rule - A rule instance
     * @returns A rule resource or null.
     */
    static async mapToRuleResourceAsync(
        client: IExtendedContentHubClient,
        rule: IRule
    ): NullableResultPromise<RuleResource> {
        if (rule == null) {
            return null;
        }

        const conditions = await ConditionMapper.mapToConditionResourcesAsync(client, rule.conditions);
        const identifier = rule.identifier;
        const permissions = rule.permissions;
        const type = RuleTypeMapper.mapToRuleResourceType(rule.type);

        const definitionLinkTasks: Array<Promise<Link>> = [];
        for (const definitionName of rule.definitionNames) {
            const definitionLink = client.linkHelper.definitionToLinkAsync(definitionName);
            definitionLinkTasks.push(definitionLink);
        }
        const definitionLinks = await Promise.all(definitionLinkTasks);

        const ruleResource = new RuleResource({
            conditions: conditions,
            createdByLoggedOnUser: rule.createdByLoggedOnUser,
            definitions: definitionLinks,
            identifier: identifier!,
            isSystemOwned: rule.isSystemOwned,
            permissions: permissions,
            type: type,
        });

        return ruleResource;
    }
}
